import { Formik, FormikProps } from "formik";
import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { FormikError, VALIDATION_TYPES } from "@/components/Formik";
import { MEDIA_SIZE_XS } from "@/core/media";
import { Flex, Group } from "@/eworkitComponents";
import { FormikForm, FormikTextInput } from "@/formikComponents";
import { Messenger } from "@/views/components/Messenger";

import { ICompanyFormikProps } from "../types";

export const validationCompanyWebsiteSectionSchema = Yup.object().shape({
  companyWebsite: Yup.string()
    .url(VALIDATION_TYPES.VALID_URL)
    .transform(currentValue => {
      const doesNotStartWithHttp =
        currentValue && !(currentValue.startsWith("http://") || currentValue.startsWith("https://"));

      if (doesNotStartWithHttp) {
        return `http://${currentValue}`;
      }
      return currentValue;
    })
    .nullable(true)
    .notRequired(),
});

export interface ICompanyWebsiteFormValues {
  companyWebsite: string;
}

export const COMPANY_WEBSITE_MESSENGER_CONTEXT = "company-website-form";

interface ICompanyWebsiteFormProps extends IEditableSectionRenderProps {
  form: FormikProps<ICompanyWebsiteFormValues>;
  onCancel: () => void;
}

export const CompanyWebsiteForm = memo((props: ICompanyWebsiteFormProps) => {
  const isVerySmallScreen = useMedia(MEDIA_SIZE_XS);
  const { t } = useTranslation("session");

  useEditableSubmit<ICompanyWebsiteFormValues>(props.form, props.editableProps);

  return (
    <FormikForm>
      <Flex direction="column" innerMargin="md" margin={[isVerySmallScreen ? "lg" : false, false, false, false]}>
        <Messenger context={COMPANY_WEBSITE_MESSENGER_CONTEXT} />

        <Flex innerMargin="md" grow={1} direction={isVerySmallScreen ? "column" : "row"}>
          <Flex direction="column" width={{ xxs: 12, xs: 5 }}>
            <Group label={t("session:COMPANY.CREATE.WEBSITE")} direction="column">
              <FormikTextInput
                type="text"
                name="companyWebsite"
                testName="companyWebsite"
                value={props.form.values.companyWebsite}
                required
                size="md"
              />
              <FormikError name="companyWebsite" data-test="error-name" />
            </Group>
          </Flex>
        </Flex>
      </Flex>
    </FormikForm>
  );
});

interface ICompanyWebsiteFormikProps extends ICompanyWebsiteFormProps, ICompanyFormikProps<ICompanyWebsiteFormValues> {}

export const CompanyWebsiteFormik: FC<ICompanyWebsiteFormikProps> = memo(
  ({ initialValues, validationSchema, onSubmit, ...rest }) => (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      render={(form: FormikProps<ICompanyWebsiteFormValues>) => <CompanyWebsiteForm {...rest} form={form} />}
    />
  ),
);
